// Make horizontally-overflowing scrollable regions keyboard-scrollable.
//
// A `.table-responsive` wider than its container scrolls sideways with a mouse or trackpad, but a
// keyboard cannot reach the scroll region because the scrolling element takes no focus — so content
// to the right of the fold is unreachable without a pointer (WCAG 2.1.1 Keyboard, level A,
// CU-1248uf3uw90).
//
// The same fault affects a handful of bespoke wide widgets that scroll via their own `overflow-x`
// rather than `.table-responsive` — roster calendars, heatmaps, charts, dataset matrices. Those
// opt in by carrying the `.sa-scrollable` class; the behaviour then treats them exactly like a
// `.table-responsive` (CU-1248uf3x33b). Because each is a distinct widget, give it a meaningful
// `aria-label` (or `aria-labelledby`) in the view — the behaviour keeps any name already set and
// only derives one when none is present.
//
// When (and only when) a target actually overflows horizontally, give it tabindex=0 + role="region"
// + an accessible name, so it joins the tab order and announces what it is rather than as an
// anonymous scrollable. Once focused, the arrow/Home/End keys scroll it natively. The name comes
// from an explicit aria-label/aria-labelledby if set, else the enclosing card's heading (or the
// nearest heading), falling back to a generic label. No visual change.
//
// Overflow depends on width AND on visibility, so this is re-evaluated by a per-element
// ResizeObserver (which also fires when a hidden target — inside a collapse, tab-pane or slideout —
// is first shown), plus whole-document scans on turbo:load / turbo:frame-load to pick up new nodes.

const SELECTOR = '.table-responsive, .sa-scrollable'

// A `.table-responsive` is always a table; an opt-in `.sa-scrollable` widget may be anything, so
// fall back to a generic region name rather than calling it a table.
const fallbackName = (el) => (el.matches('.table-responsive') ? 'Scrollable table' : 'Scrollable region')

const clean = (text) => text.replace(/\s+/g, ' ').trim()

// Derive a name from the table's context: the enclosing card's heading (or header text), else the
// nearest preceding heading walking up to the section/card boundary, else a generic fallback.
const nameFor = (el) => {
  const header = el.closest('.card')?.querySelector('.card-header')
  if (header) {
    const heading = header.querySelector('h1, h2, h3, h4, h5, h6')
    const text = clean((heading || header).textContent)
    if (text) return text
  }

  let node = el
  while (node && !node.matches?.('main, body')) {
    for (let sib = node.previousElementSibling; sib; sib = sib.previousElementSibling) {
      const heading = sib.matches?.('h1, h2, h3, h4, h5, h6')
        ? sib
        : sib.querySelector?.('h1, h2, h3, h4, h5, h6')
      if (heading) {
        const text = clean(heading.textContent)
        if (text) return text
      }
    }
    if (node.matches?.('section, .card')) break // don't reach past the element's own section/card
    node = node.parentElement
  }
  return fallbackName(el)
}

const update = (el) => {
  const overflowing = el.scrollWidth > el.clientWidth + 1 // +1 for sub-pixel rounding

  if (overflowing && el.dataset.saScrollable !== 'on') {
    el.dataset.saScrollable = 'on'
    // Only add — and later remove — the attributes the view did not supply itself.
    if (!el.hasAttribute('tabindex')) {
      el.setAttribute('tabindex', '0')
      el.dataset.saTabindex = 'auto'
    }
    if (!el.hasAttribute('role')) {
      el.setAttribute('role', 'region')
      el.dataset.saRole = 'auto'
    }
    if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
      el.setAttribute('aria-label', nameFor(el))
      el.dataset.saName = 'auto'
    }
  } else if (!overflowing && el.dataset.saScrollable === 'on') {
    // No longer overflowing (e.g. widened viewport) — remove only what we added, so there is no
    // dead tab stop or misleading region.
    el.dataset.saScrollable = 'off'
    if (el.dataset.saTabindex === 'auto') { el.removeAttribute('tabindex'); delete el.dataset.saTabindex }
    if (el.dataset.saRole === 'auto') { el.removeAttribute('role'); delete el.dataset.saRole }
    if (el.dataset.saName === 'auto') { el.removeAttribute('aria-label'); delete el.dataset.saName }
  }
}

const observed = new Set()
const observer = typeof ResizeObserver !== 'undefined'
  ? new ResizeObserver((entries) => entries.forEach((entry) => update(entry.target)))
  : null

const scan = (container = document) => {
  // Drop observations for tables that have left the DOM (e.g. a turbo-frame swap).
  if (observer) {
    observed.forEach((el) => {
      if (!el.isConnected) { observer.unobserve(el); observed.delete(el) }
    })
  }
  container.querySelectorAll(SELECTOR).forEach((el) => {
    update(el)
    if (observer && !observed.has(el)) {
      // observe() fires immediately, and again when the element is shown or its width changes —
      // which is how a table hidden at scan time (collapse/tab/slideout) gets handled on reveal.
      observer.observe(el)
      observed.add(el)
    }
  })
}

document.addEventListener('turbo:load', () => scan())
document.addEventListener('turbo:frame-load', (event) => scan(event.target))

// Fallback for browsers without ResizeObserver: re-scan on window resize (debounced).
if (!observer) {
  let resizeTimer
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => scan(), 150)
  })
}
